import { Loader, Tooltip } from '@mantine/core';
import { IconCircleCheckFilled, IconCircleXFilled, IconInbox } from '@tabler/icons-react';
import { BackgroundProcess } from 'src/data-capture/hooks/file-uploads/types';

type StatusKey = BackgroundProcess['status'] | 'UNFILED';

type Props = {
  status: StatusKey;
  tooltipLabel?: string;
  processingTooltipLabel?: string;
};

const defaultLabels: Record<StatusKey, string> = {
  PENDING: 'Extracting data from document...',
  PROCESSING: 'Extracting data from document...',
  SUCCESS: 'Data extracted - document is ready',
  FAILED: 'Processing failed',
  UNFILED: 'Document is waiting to be filed',
};

function StatusIcon({ status }: { status: StatusKey }) {
  switch (status) {
    case 'PENDING':
    case 'PROCESSING':
      return <Loader size={18} />;
    case 'SUCCESS':
      return <IconCircleCheckFilled size={20} color="var(--mantine-color-green-6)" />;
    case 'FAILED':
      return <IconCircleXFilled size={20} color="var(--mantine-color-red-6)" />;
    case 'UNFILED':
      return <IconInbox size={20} color="var(--mantine-color-gray-5)" />;
  }
}

function resolveLabel(status: StatusKey, tooltipLabel?: string, processingTooltipLabel?: string) {
  if (tooltipLabel) return tooltipLabel;
  if (processingTooltipLabel && (status === 'PENDING' || status === 'PROCESSING')) {
    return processingTooltipLabel;
  }
  return defaultLabels[status];
}

export default function FileStatusIcon({ status, tooltipLabel, processingTooltipLabel }: Props) {
  return (
    <Tooltip
      label={resolveLabel(status, tooltipLabel, processingTooltipLabel)}
      multiline
      maw={400}
      withArrow
    >
      <span
        style={{ display: 'inline-flex', alignItems: 'center' }}
        tabIndex={0}
        data-file-status={status}
      >
        <StatusIcon status={status} />
      </span>
    </Tooltip>
  );
}
